import { useState } from "react";
import type { MarketplaceOrder, MarketplaceCategory } from "../types";
import { FULFILLMENT_HINT_KEYS, CATEGORY_EMOJI } from "../constants";
import { useLocale } from "../../../context/LanguageContext";

interface FulfillOrderDialogProps {
  order: MarketplaceOrder;
  onFulfill: (fulfillmentDetails: string) => void;
  onClose: () => void;
  loading: boolean;
}

export function FulfillOrderDialog({
  order,
  onFulfill,
  onClose,
  loading,
}: FulfillOrderDialogProps) {
  const [details, setDetails] = useState("");
  const { t } = useLocale();
  const category = (order.item?.category ?? "OTHER") as MarketplaceCategory;
  const hint = t(FULFILLMENT_HINT_KEYS[category] ?? FULFILLMENT_HINT_KEYS.OTHER);
  const emoji = CATEGORY_EMOJI[category] ?? "📦";
  const adminInstructions = order.item?.fulfillmentInstructions;

  const userName = order.user
    ? `${order.user.firstName ?? ""} ${order.user.lastName ?? ""}`.trim() ||
      order.user.email ||
      order.userId.slice(-8)
    : order.userId.slice(-8);

  const steps = [
    {
      num: 1,
      title: t("fulfillDialog.step1"),
      desc: t("fulfillDialog.step1Desc", { userName, item: order.item?.name ?? t("common.unknown"), koins: order.koinsSpent.toLocaleString() }),
    },
    {
      num: 2,
      title: t("fulfillDialog.step2"),
      desc: hint,
    },
    {
      num: 3,
      title: t("fulfillDialog.step3"),
      desc: t("fulfillDialog.step3Desc"),
    },
    {
      num: 4,
      title: t("fulfillDialog.step4"),
      desc: t("fulfillDialog.step4Desc"),
    },
  ];

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-foreground">
          {emoji} {t("fulfillDialog.guideTitle")}
        </h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {t("fulfillDialog.guideSubtitle")}
        </p>

        {/* Admin instructions callout */}
        {adminInstructions && (
          <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm">
            <span className="font-medium text-primary">
              {t("fulfillDialog.adminInstructions")}
            </span>
            <p className="mt-1 text-foreground">{adminInstructions}</p>
          </div>
        )}

        {/* Steps */}
        <div className="mt-5 space-y-3">
          {steps.map((step) => (
            <div key={step.num} className="flex gap-3">
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                {step.num}
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground">
                  {step.title}
                </h4>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {step.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Fulfillment details input */}
        <div className="mt-5">
          <label className="mb-1.5 block text-sm font-medium text-foreground">
            {t("fulfillDialog.detailsLabel")}{" "}
            <span className="text-xs text-muted-foreground">
              {t("fulfillDialog.detailsHint")}
            </span>
          </label>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder={t("fulfillDialog.detailsPlaceholder")}
          />
        </div>

        {/* Actions */}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-md px-4 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {t("fulfillDialog.cancel")}
          </button>
          <button
            type="button"
            onClick={() => onFulfill(details)}
            disabled={loading}
            className="h-9 rounded-md bg-success px-4 text-sm font-medium text-success-foreground hover:bg-success/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? t("fulfillDialog.fulfilling") : t("fulfillDialog.confirmFulfill")}
          </button>
        </div>
      </div>
    </>
  );
}
